import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCandidateService } from 'domain/shared/candidate';
import { useCrudService } from 'framework/crud';
import { useRecruitmentApi } from 'infrastructure/api';
import { StepIconProps, Stepper, SvgIcon, Typography, Step, StepLabel } from '@mui/material';
import { columnNames } from 'domain/recruiting/recruitingBoard/shared';
import { Stages } from 'domain/shared/candidate';
import { useVacancyGroupService } from 'domain/shared/newVacancy';
import { LoadingDecorator } from 'framework/ui/ui-builder';
import { CROP_SIZES, useArtifactInfoService } from 'domain/shared/artifact';
import { useVacancyGroupUrl } from 'domain/recruiting/newVacancy/routing';
import { Box } from '@mui/material';
import { LinkField } from 'framework/ui/field';
import CandidateView from '../candidateView/CandidateView';
import CandidateCard from './CandidateCard';
import _ from 'lodash';
import useCandidateApplicationFromExtensionService from 'domain/recruiting/candidate/applications/shared/candidateApplicationFromExtensionService';
import { ShowPageLayout, ShowSection } from 'framework/ui/layout';
import ApplyToVacancyForm from 'domain/recruiting/extension/ApplyToVacancyForm';
import CandidateVacancyRejectForm from '../candidateView/CandidateVacancyRejectForm/CandidateVacancyRejectForm';
import { CandidateData } from 'domain/recruiting/extension/candidateFromExtensionService';

interface VacancyDto {
  id?: string;
  specialization?: string;
  details?: {
    competencyLevelMax?: number;
    competencyLevelMin?: number;
  };
}

interface ApplicationDto {
  vacancyGroupAlias?: string;
  applicationPipelineState?: {
    stage?: string;
    rejectedFrom?: string;
  };
}

interface CandidateDto {
  id: string;
  mainPhotoId?: string;
}

interface CandidateMatchDto {
  candidate: CandidateDto;
  score: {
    personalPhoneNumber?: number;
    personalEmail?: number;
    otherEmail?: number;
  };
}

interface CandidateContext extends CandidateData {
  id?: string;
  vacancy?: VacancyDto;
  application?: ApplicationDto;
}

const getPossibleLinkedInLinks = (linkedInId: string) => [
  linkedInId,
  `https://linkedin.com/in/${linkedInId}`,
  `https://linkedin.com/in/${linkedInId}/`,
  `https://www.linkedin.com/in/${linkedInId}`,
  `https://www.linkedin.com/in/${linkedInId}/`,
  `linkedin.com/in/${linkedInId}`,
  `linkedin.com/in/${linkedInId}/`,
  `www.linkedin.com/in/${linkedInId}`,
  `www.linkedin.com/in/${linkedInId}/`,
];

const getNameFromFullName = (fullName: string) => {
  const [firstName, ...lastNameParts] = fullName.trim().split(/\s+/).filter(Boolean);

  if (!firstName) {
    return {};
  }

  const lastName = lastNameParts.join(' ');

  return {
    name: {
      firstName,
      ...(lastName ? { lastName } : {}),
    },
  };
};

const isExactContactMatch = ({ score }: CandidateMatchDto) =>
  Boolean(score.personalPhoneNumber || score.personalEmail || score.otherEmail);

const CustomStepIcon = ({ active, completed, error }: StepIconProps) => {
  const getColor = () => {
    switch (true) {
      case completed:
        return 'primary.main';
      case error:
        return 'error.main';
      case active:
        return 'primary.dark';
      default:
        return 'text.disabled';
    }
  };

  return (
    <SvgIcon
      sx={{
        width: 15,
        height: 15,
        color: getColor(),
        margin: 0.5,
      }}
    >
      <circle cx="14" cy="10" r="9" />
    </SvgIcon>
  );
};
const ApplicationStatusStepper = ({
  activeStep,
  rejectedStep,
  steps,
}: {
  activeStep: string;
  rejectedStep: string;
  steps: string[];
}) => {
  let stepIndex = steps.indexOf(activeStep);
  if (stepIndex === -1) {
    stepIndex = steps.indexOf(rejectedStep);
  }

  return (
    <Stepper activeStep={stepIndex} alternativeLabel>
      {steps.map(step => (
        <Step
          key={step}
          sx={{
            p: 0,
            '& .MuiStepLabel-iconContainer': {
              pr: 0,
            },
          }}
        >
          <StepLabel
            slots={{
              stepIcon: CustomStepIcon,
            }}
            error={rejectedStep === step}
            optional={
              rejectedStep === step ? (
                <Typography variant="body2" color="error">
                  Rejected
                </Typography>
              ) : null
            }
          >
            <Typography variant="subtitle1">{step}</Typography>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};

const CandidateContextPage = () => {
  const linkedInId = useParams<Dictionary<string>>().linkedInId!;
  const [searchParams] = useSearchParams();
  const fullName = searchParams.get('fullName') || '';
  const { search: searchCandidates } = useCandidateService();
  const { search: searchVacancyGroup } = useVacancyGroupService();
  const { search: searchApplications } = useCrudService({
    singleResourceUrl: '/candidates/applications/:id',
    listResourceUrl: '/candidates/applications',
    useService: useRecruitmentApi,
  });
  const { getThumbnails } = useArtifactInfoService();
  const { getSingleEntityUrl } = useVacancyGroupUrl();
  const { isParsingFinished, parsedProfileData } = useCandidateApplicationFromExtensionService();
  const { getDuplicates } = useCandidateService();
  const [candidate, setCandidate] = useState<Nullable<CandidateContext>>(null);
  const [duplicates, setDuplicates] = useState([]);
  const [candidatePhoto, setCandidatePhoto] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [showVacancyReject, setShowVacancyReject] = useState<boolean>(false);
  const searchRef = useRef({ linkedInDone: false, found: false, nameSearched: false });

  const parsedDataFromFullName = useMemo(() => getNameFromFullName(fullName), [fullName]);
  const effectiveParsedProfileData = useMemo(
    () => (!_.isEmpty(parsedProfileData) ? parsedProfileData : parsedDataFromFullName),
    [parsedDataFromFullName, parsedProfileData],
  );
  const isEffectiveParsingFinished = isParsingFinished || !_.isEmpty(parsedDataFromFullName);

  const togleVacancyReject = () => {
    setShowVacancyReject(prev => !prev);
  };

  useEffect(() => {
    (async () => {
      const data = effectiveParsedProfileData as CandidateData;
      const hasName = Boolean(data?.name?.firstName || data?.name?.lastName);
      const hasContact = Boolean(data?.personalEmail || data?.personalPhoneNumber);
      const isParsedCorrectly =
        isEffectiveParsingFinished && !_.isEmpty(effectiveParsedProfileData) && (hasName || hasContact);
      if (!isParsedCorrectly) {
        return;
      }

      const matchesPayload = {
        ...effectiveParsedProfileData,
        name: {
          ...data?.name,
          firstName: data?.name?.firstName || '',
          lastName: data?.name?.lastName || '',
        },
      };

      try {
        const matches = await getDuplicates(matchesPayload);
        setDuplicates(matches);

        const exactContactMatch = (matches as CandidateMatchDto[]).find(isExactContactMatch);
        if (exactContactMatch && !searchRef.current.found) {
          searchRef.current.found = true;
          await hydrateCandidate(exactContactMatch.candidate);
        }
      } catch {
        setDuplicates([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveParsedProfileData, isEffectiveParsingFinished]);

  const getNameForSearch = () => {
    const parsedName = (effectiveParsedProfileData as CandidateData)?.name;
    const composed = [parsedName?.firstName, parsedName?.lastName].filter(Boolean).join(' ').trim();
    return composed || fullName;
  };

  const hydrateCandidate = async (candidateDto: CandidateDto) => {
    let application: Nullable<ApplicationDto>;
    try {
      [application] = (
        await searchApplications({
          filter: {
            candidateId: { eq: candidateDto.id },
          },
          sort: {
            updatedAt: 'DESC',
          },
          pageSize: 1,
        })
      ).result;
    } catch {
      application = null;
    }

    let vacancy: VacancyDto | undefined;
    try {
      if (application?.vacancyGroupAlias) {
        [vacancy] = (
          await searchVacancyGroup({
            filter: {
              alias: { eq: application?.vacancyGroupAlias },
            },
            sort: { createdAt: 'DESC' },
            pageSize: 1,
          })
        ).result;
      }
    } catch {
      vacancy = undefined;
    }

    if (candidateDto?.mainPhotoId) {
      try {
        const thumbnailsResponse = await getThumbnails(candidateDto?.mainPhotoId);
        setCandidatePhoto(thumbnailsResponse[CROP_SIZES.SIZE_100]);
      } catch {}
    }

    setCandidate({
      ...candidateDto,
      vacancy,
      application: application ?? undefined,
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const state = searchRef.current;

      if (!state.linkedInDone) {
        const response = await searchCandidates({
          filter: { linkedInLink: { in: getPossibleLinkedInLinks(linkedInId) } },
          sort: { updatedAt: 'DESC' },
        });
        if (cancelled) {
          return;
        }
        state.linkedInDone = true;
        const [candidateDto] = response.result || [];
        if (candidateDto) {
          state.found = true;
          await hydrateCandidate(candidateDto);
        }
        if (!cancelled) {
          setLoading(false);
        }
      }

      if (cancelled || state.found || state.nameSearched || !isEffectiveParsingFinished) {
        return;
      }
      const nameForSearch = getNameForSearch().trim();
      if (!nameForSearch) {
        return;
      }
      state.nameSearched = true;

      const response = await searchCandidates({
        filter: { searchString: { ct: nameForSearch } },
        sort: { updatedAt: 'DESC' },
      });
      let results = response.result || [];
      if (results.length > 1) {
        // As it is not an exact match, let recruiter choose
        results = [];
      }
      const [candidateDto] = results;
      if (candidateDto && !cancelled) {
        state.found = true;
        await hydrateCandidate(candidateDto);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEffectiveParsingFinished, effectiveParsedProfileData]);

  const steps = columnNames.map(column => column.name);

  const specializationTitle = () => {
    if (!candidate?.vacancy?.specialization) {
      return '';
    }

    const competencyLevelMax = candidate?.vacancy?.details?.competencyLevelMax;
    const competencyLevelMin = candidate?.vacancy?.details?.competencyLevelMin;
    const specialization = candidate?.vacancy?.specialization;

    return competencyLevelMax === competencyLevelMin
      ? `${competencyLevelMax} ${specialization}`
      : `${competencyLevelMin} - ${competencyLevelMax} ${specialization}`;
  };

  const applicationStage = candidate?.application?.applicationPipelineState?.stage || '';
  const rejectedFrom = candidate?.application?.applicationPipelineState?.rejectedFrom || '';
  const isClosedOrRejected = applicationStage === Stages.Closed || applicationStage === Stages.Rejected;

  if (!candidate?.id && !loading) {
    return (
      <CandidateView
        duplicates={duplicates}
        isParsingFinished={isEffectiveParsingFinished}
        parsedProfileData={effectiveParsedProfileData}
      />
    );
  }

  return (
    <LoadingDecorator loading={loading} deps={[loading]}>
      {candidate?.id && (
        <ShowPageLayout>
          <ShowSection sx={{ mb: '10px !important', pb: '0px !important' }}>
            <CandidateCard
              candidate={candidate}
              candidatePhoto={candidatePhoto}
              showVacancyReject={showVacancyReject}
              togleVacancyReject={togleVacancyReject}
              showVacancyRejectButton
            />
          </ShowSection>
          <ShowSection>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', p: 1, pt: 0 }}>
              {showVacancyReject ? (
                <ShowSection>
                  <CandidateVacancyRejectForm togleVacancyReject={togleVacancyReject} />
                </ShowSection>
              ) : (
                <>
                  <Box sx={{ pb: 2 }}>
                    {candidate?.vacancy?.id ? (
                      <LinkField linkText={specializationTitle()} value={getSingleEntityUrl(candidate?.vacancy?.id)} />
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        {specializationTitle()}
                      </Typography>
                    )}
                  </Box>
                  <Box width="100%">
                    <ApplicationStatusStepper activeStep={applicationStage} rejectedStep={rejectedFrom} steps={steps} />
                  </Box>
                  {isClosedOrRejected && (
                    <Box width="100%" sx={{ pt: 2 }}>
                      <ApplyToVacancyForm parsedProfileData={candidate} isParsingFinished />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </ShowSection>
        </ShowPageLayout>
      )}
    </LoadingDecorator>
  );
};

export default CandidateContextPage;
